import React from "react";
import axios from "axios";
import { Star, Quote, MessageSquarePlus, X, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL;

function Initials({ name, lang }) {
  const isAr = lang === "ar" || /[\u0600-\u06FF]/.test(name || "");
  const words = (name || "").split(/\s+/).filter(Boolean);
  const initials = isAr
    ? (words[0]?.[0] || "·")
    : words.slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
  return (
    <div
      aria-hidden
      className="w-12 h-12 rounded-full bg-[#C86240]/15 text-[#C86240] flex items-center justify-center font-semibold text-lg shrink-0"
    >
      {initials}
    </div>
  );
}

function TestimonialCard({ item }) {
  const isAr = item.lang === "ar";
  const dirClass = isAr ? "text-right" : "text-left";
  const fontClass = isAr ? "font-arabic" : "font-latin";
  return (
    <article
      id={`testimonial-${item.id}`}
      className={`group relative rounded-2xl bg-white border border-[#2A2421]/8 p-6 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 flex flex-col gap-4 scroll-mt-24 ${dirClass} ${fontClass}`}
      dir={isAr ? "rtl" : "ltr"}
      data-testid={`testimonial-card-${item.id}`}
    >
      <Quote
        className={`absolute top-4 ${isAr ? "left-4" : "right-4"} text-[#C86240]/15`}
        size={32}
      />
      <div className="flex items-center gap-1.5 text-[#C86240]">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={14}
            className={i < (item.rating || 5) ? "fill-[#C86240]" : "opacity-30"}
          />
        ))}
      </div>
      <p className="text-[15px] sm:text-base text-[#2A2421] leading-relaxed flex-1 whitespace-pre-line">
        {item.text}
      </p>
      <div className="flex items-center gap-3 pt-3 border-t border-[#2A2421]/8">
        {item.photo_url ? (
          <img
            src={item.photo_url}
            alt={item.name}
            className="w-12 h-12 rounded-full object-cover border border-[#2A2421]/10 shrink-0"
            loading="lazy"
          />
        ) : (
          <Initials name={item.name} lang={item.lang} />
        )}
        <div className="min-w-0">
          <div className="font-semibold text-sm sm:text-base text-[#2A2421] truncate">
            {item.name}
          </div>
          {item.cafe && (
            <div className="text-xs sm:text-sm text-[#5C544F] truncate">
              {item.cafe}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function StarPicker({ value, onChange, ariaLabel }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1 rounded hover:bg-[#C86240]/10 transition-colors"
          aria-label={`${n} stars`}
          data-testid={`form-star-${n}`}
        >
          <Star
            size={28}
            className={n <= value ? "fill-[#C86240] text-[#C86240]" : "text-[#2A2421]/25"}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ onClose, onSubmitted }) {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const [name, setName] = React.useState("");
  const [cafe, setCafe] = React.useState("");
  const [text, setText] = React.useState("");
  const [rating, setRating] = React.useState(5);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error(t.testimonials.tooShort);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/testimonials`, {
        name: name.trim(),
        cafe: cafe.trim() || null,
        text: text.trim(),
        rating,
        lang,
        order: 999,
      });
      toast.success(t.testimonials.thanks);
      onSubmitted?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="review-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        dir={ar ? "rtl" : "ltr"}
        className={`relative w-full max-w-lg bg-white rounded-3xl border border-[#2A2421]/10 p-6 sm:p-8 shadow-2xl ${
          ar ? "text-right" : "text-left"
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className={`absolute top-3 ${ar ? "left-3" : "right-3"} w-9 h-9 rounded-full hover:bg-[#2A2421]/5 flex items-center justify-center text-[#5C544F]`}
          data-testid="close-review-modal"
        >
          <X size={18} />
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C86240]/10 border border-[#C86240]/20 text-[#C86240] text-[11px] uppercase tracking-widest mb-4">
          <MessageSquarePlus size={12} />
          {t.testimonials.writeReview}
        </div>
        <h3
          className={`${ar ? "font-serif-ar" : "font-display"} text-2xl sm:text-3xl text-[#2A2421] mb-2`}
        >
          {t.testimonials.formTitle}
        </h3>
        <p className="text-sm text-[#5C544F] mb-5">{t.testimonials.formLead}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#5C544F] mb-1.5">
              {t.testimonials.yourName} *
            </label>
            <input
              required
              minLength={2}
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-[#2A2421]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86240]/40 focus:border-[#C86240]/40"
              data-testid="review-name"
              dir={ar ? "rtl" : "ltr"}
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#5C544F] mb-1.5">
              {t.testimonials.yourRole}
            </label>
            <input
              maxLength={80}
              value={cafe}
              onChange={(e) => setCafe(e.target.value)}
              className="w-full rounded-xl border border-[#2A2421]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86240]/40 focus:border-[#C86240]/40"
              data-testid="review-cafe"
              dir={ar ? "rtl" : "ltr"}
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#5C544F] mb-1.5">
              {t.testimonials.rating || "Rating"}
            </label>
            <StarPicker value={rating} onChange={setRating} ariaLabel="rating" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#5C544F] mb-1.5">
              {t.testimonials.yourReview} *
            </label>
            <textarea
              required
              minLength={10}
              maxLength={1000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t.testimonials.reviewPlaceholder}
              rows={5}
              className="w-full rounded-xl border border-[#2A2421]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86240]/40 focus:border-[#C86240]/40 resize-y"
              data-testid="review-text"
              dir="auto"
            />
            <div className="text-[11px] text-[#5C544F]/70 mt-1 text-end">
              {text.length} / 1000
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-full text-sm text-[#5C544F] hover:bg-[#2A2421]/5 transition-colors"
            data-testid="cancel-review"
          >
            {t.common.cancel}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] text-sm font-medium disabled:opacity-60 transition-colors"
            data-testid="submit-review"
          >
            <Send size={14} />
            {submitting ? "…" : t.testimonials.submit}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Testimonials() {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);

  const load = React.useCallback(() => {
    axios
      .get(`${API}/api/testimonials`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // Scroll to a specific testimonial when URL hash matches (admin "go to source").
  React.useEffect(() => {
    if (!items?.length) return;
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    const m = hash.match(/^#testimonial-(.+)$/);
    if (!m) return;
    const el = document.getElementById(`testimonial-${m[1]}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#C86240]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#C86240]", "ring-offset-2"), 2400);
      }, 250);
    }
  }, [items?.length]);

  return (
    <section
      className="bg-[#F9F6F0] border-t border-[#2A2421]/8"
      data-testid="testimonials-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C86240]/10 border border-[#C86240]/20 text-[#C86240] text-[11px] uppercase tracking-widest mb-4">
              <Star size={12} className="fill-[#C86240]" />
              {t.testimonials.eyebrow}
            </div>
            <h2
              className={`${ar ? "font-serif-ar" : "font-display"} text-3xl sm:text-4xl text-[#2A2421] leading-tight mb-3`}
            >
              {t.testimonials.title}
            </h2>
            <p className="text-sm sm:text-base text-[#5C544F] max-w-2xl leading-relaxed">
              {t.testimonials.lead}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white text-sm font-medium shadow-sm hover:shadow-md transition-all shrink-0"
            data-testid="write-review-btn"
          >
            <MessageSquarePlus size={16} />
            {t.testimonials.writeReview}
          </button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-52 rounded-2xl bg-white/60 border border-[#2A2421]/8 animate-pulse"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2A2421]/15 bg-white/50 p-10 text-center">
            <p className="text-[#5C544F] mb-5">{t.testimonials.empty}</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] text-sm font-medium"
              data-testid="empty-write-btn"
            >
              <MessageSquarePlus size={14} />
              {t.testimonials.writeReview}
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <ReviewForm
          onClose={() => setShowForm(false)}
          onSubmitted={() => load()}
        />
      )}
    </section>
  );
}
